import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
// import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography"; // Poolcorp Custom Code
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { css } from "styled-components";

/* Poolcorp Custom Code */
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { TYPOGRAPHY } from "@store/theme/design";
/* Poolcorp Custom Code */

interface OwnProps {
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    showProductNumber?: boolean;
    // Poolcorp Custom Code
    centerText?: boolean;
}

type Props = OwnProps;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    // Poolcorp Custom Code
    ellipsisWrapper?: TypographyProps;
}

/* Poolcorp Custom Code */
const productPartNumberLabelProps: TypographyProps = {
    as: "span",
    size: TYPOGRAPHY.size.tiny,
    weight: TYPOGRAPHY.weight.base,
    color: "text.accent",
    css: css`
        margin-right: 0.3em;
    `,
};

const productPartNumberValueProps: TypographyProps = {
    as: "span",
    size: TYPOGRAPHY.size.tiny,
    weight: TYPOGRAPHY.weight.base,
    color: "text.accent",
};
/* Poolcorp Custom Code */

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    container: { gap: 0 },
    erpNumberLabelText: productPartNumberLabelProps,
    erpNumberValueText: productPartNumberValueProps,
    customerNameLabelText: productPartNumberLabelProps,
    customerNameValueText: productPartNumberValueProps,
    manufacturerItemLabelText: productPartNumberLabelProps,
    manufacturerItemValueText: productPartNumberValueProps,
    ellipsisWrapper: {
        ellipsis: true,
    },
};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    extendedStyles,
    showProductNumber = true,
    // Poolcorp Custom Code
    centerText = false,
}) => {
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;

    /* Poolcorp Cutom Code */
    const partNumberLabel = `${translate("Part #")}${translate(":")}`;
    const myPartNumberLabel = `${translate("My Part #")}${translate(":")}`;
    const mfgNumberLabel = `${translate("MFG #")}${translate(":")}`; // eslint-disable-line spire/avoid-dynamic-translate

    const itemJustifyMode = centerText ? 'center' : 'flex-start';

    const productPartNumberItemProps: GridItemProps = {
        css: css`
            flex-flow: wrap;
            justify-content: ${itemJustifyMode};
        `,
    };
    /* Poolcorp Cutom Code */

    return (
        <GridContainer {...styles.container}>
            {showProductNumber && (
            <GridItem width={gridItemWidth as GridWidths} {...productPartNumberItemProps} /* Poolcorp Custom Code */>
                <Typography {...styles.ellipsisWrapper}>
                    {showLabel && <Typography {...styles.erpNumberLabelText}>{partNumberLabel}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.erpNumberLabelText}>{partNumberLabel}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                        {productNumber}
                    </Typography>
                </Typography>
            </GridItem>
            )}
            {displayCustomerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...productPartNumberItemProps} /* Poolcorp Custom Code */>
                    <Typography {...styles.ellipsisWrapper}>
                        {showLabel && <Typography {...styles.customerNameLabelText}>{myPartNumberLabel}</Typography>}
                        {!showLabel && (
                            <VisuallyHidden>
                                <Typography {...styles.customerNameLabelText}>{myPartNumberLabel}</Typography>
                            </VisuallyHidden>
                        )}
                        <Typography {...styles.customerNameValueText}  data-test-selector="customerProductNumber">
                            {customerProductNumber}
                        </Typography>
                    </Typography>
                </GridItem>
            )}
            {displayManufacturerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...productPartNumberItemProps} /* Poolcorp Custom Code */>
                    <Typography {...styles.ellipsisWrapper}>
                        {showLabel && <Typography {...styles.manufacturerItemLabelText}>{mfgNumberLabel}</Typography>}
                        {!showLabel && (
                            <VisuallyHidden>
                                <Typography {...styles.manufacturerItemLabelText}>{mfgNumberLabel}</Typography>
                            </VisuallyHidden>
                        )}
                        <Typography {...styles.manufacturerItemValueText} data-test-selector="manufacturerItem">
                            {manufacturerItem}
                        </Typography>
                    </Typography>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default ProductPartNumbers;
